import { Plus, Edit2, Trash2, CheckCircle2, Circle, RotateCcw } from "lucide-react";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import Modal from "../../../components/ui/Modal";

interface DailyRoutine {
    id: string;
    text: string;
    priority: "low" | "medium" | "high" | "critical";
    color: string;
    completedToday: boolean;
    order: number;
    createdAt: string;
}

interface DailyRoutineItemProps {
    routine: DailyRoutine;
    onToggleComplete: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    canToggle: boolean;
    canUpdate: boolean;
    canDelete: boolean;
}

const priorityConfig = {
    low: { label: "Düşük", color: "bg-gray-100 text-gray-700", border: "border-gray-200" },
    medium: { label: "Orta", color: "bg-yellow-100 text-yellow-700", border: "border-yellow-200" },
    high: { label: "Yüksek", color: "bg-orange-100 text-orange-700", border: "border-orange-200" },
    critical: { label: "Kritik", color: "bg-red-100 text-red-700", border: "border-red-200" },
};

export function DailyRoutineItem({
    routine,
    onToggleComplete,
    onEdit,
    onDelete,
    canToggle,
    canUpdate,
    canDelete,
}: DailyRoutineItemProps) {
    const priority = priorityConfig[routine.priority];

    return (
        <div
            className={`group bg-white rounded-xl border-2 px-4 py-3 shadow-sm hover:shadow-md transition-all duration-200 ${routine.completedToday ? "opacity-70 border-green-300 bg-green-50/30" : priority.border
                }`}
        >
            <div className="flex items-center gap-3">

                <button
                    onClick={() => onToggleComplete(routine.id)}
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${canToggle ? "cursor-pointer hover:scale-110" : "cursor-not-allowed opacity-50"
                        } ${routine.completedToday ? "text-green-600" : "text-gray-400 hover:text-gray-600"}`}
                    disabled={!canToggle}
                >
                    {routine.completedToday ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
                </button>

                <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: routine.color }} />

                <div className="flex-1 min-w-0">
                    <p
                        className={`text-sm font-medium ${routine.completedToday ? "line-through text-gray-500" : "text-gray-900"
                            }`}
                    >
                        {routine.text}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priority.color}`}>
                            {priority.label}
                        </span>
                        {routine.completedToday && (
                            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Bugün tamamlandı
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canUpdate && (
                        <button
                            onClick={() => onEdit(routine.id)}
                            className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                            title="Düzenle"
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => onDelete(routine.id)}
                            className="p-1.5 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                            title="Sil"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

interface DailyRoutineFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    text: string;
    setText: (text: string) => void;
    priority: "low" | "medium" | "high" | "critical";
    setPriority: (priority: "low" | "medium" | "high" | "critical") => void;
    color: string;
    setColor: (color: string) => void;
    onSubmit: () => void;
    submitText: string;
    isLoading?: boolean;
}

export function DailyRoutineFormModal({
    isOpen,
    onClose,
    title,
    text,
    setText,
    priority,
    setPriority,
    color,
    setColor,
    onSubmit,
    submitText,
    isLoading,
}: DailyRoutineFormModalProps) {
    const colors = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
            <div className="space-y-4">

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rutin *</label>
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Günlük rutin açıklaması..."
                        autoFocus
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Öncelik</label>
                    <div className="flex gap-2 flex-wrap">
                        {(["low", "medium", "high", "critical"] as const).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPriority(p)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${priority === p
                                    ? `${priorityConfig[p].color} ring-2 ring-offset-1 ring-gray-400`
                                    : `${priorityConfig[p].color} opacity-60 hover:opacity-100`
                                    }`}
                            >
                                {priorityConfig[p].label}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Renk</label>
                    <div className="flex gap-2">
                        {colors.map((c) => (
                            <button
                                key={c}
                                onClick={() => setColor(c)}
                                className={`w-8 h-8 rounded-full transition-all ${color === c ? "ring-2 ring-offset-2 ring-gray-600 scale-110" : "hover:scale-105"
                                    }`}
                                style={{ backgroundColor: c }}
                            />
                        ))}
                    </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                        <RotateCcw className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-purple-700">
                            <p className="font-medium">Günlük Rutin Nasıl Çalışır?</p>
                            <p className="mt-1 text-purple-600">
                                Bu rutin her gün otomatik olarak "tamamlanmadı" durumuna döner.
                                Silmediğiniz sürece günlük görev listenizde kalır.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                    >
                        İptal
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={!text.trim() || isLoading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading ? "Kaydediliyor..." : submitText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

interface DailyRoutineDeleteConfirmProps {
    isOpen: boolean;
    routineName: string;
    onClose: () => void;
    onConfirm: () => void;
}

export function DailyRoutineDeleteConfirm({
    isOpen,
    routineName,
    onClose,
    onConfirm,
}: DailyRoutineDeleteConfirmProps) {
    return (
        <ConfirmModal
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
            title="Rutin Sil"
            message={`"${routineName}" rutinini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm tamamlanma geçmişi silinecektir.`}
            confirmText="Sil"
            cancelText="İptal"
            confirmButtonVariant="danger"
        />
    );
}